import type { ComponentChildren, JSX, Ref } from 'preact';
import { useId } from 'preact/hooks';
import { cn } from '@/lib/cn';

export interface SwitchProps extends Omit<JSX.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
  label?: ComponentChildren;
  description?: ComponentChildren;
  inputRef?: Ref<HTMLInputElement>;
}

export function Switch({
  label,
  description,
  className,
  id,
  disabled,
  checked,
  inputRef,
  ...rest
}: SwitchProps) {
  const name = typeof rest.name === 'string' ? rest.name : 'x';
  // Estable entre renders: con Math.random() el id (y el htmlFor) cambiaba en
  // cada render y el DOM se reescribía sin necesidad.
  const generatedId = useId();
  const autoId = id ?? `sw-${name}-${generatedId}`;
  return (
    <label
      htmlFor={autoId}
      className={cn(
        'group inline-flex items-start gap-3 cursor-pointer select-none',
        disabled && 'opacity-60 cursor-not-allowed',
        className,
      )}
    >
      <span
        className={cn(
          'relative mt-0.5 inline-flex h-5 w-8 shrink-0 items-center rounded-full border transition-colors duration-med ease-snappy',
          checked ? 'border-brand bg-brand' : 'border-border-subtle bg-surface-2',
        )}
      >
        <input
          ref={inputRef}
          id={autoId}
          type="checkbox"
          role="switch"
          disabled={disabled}
          checked={checked}
          className="absolute inset-0 appearance-none cursor-pointer rounded-full focus-visible:outline-none focus-visible:shadow-focus disabled:cursor-not-allowed"
          {...rest}
        />
        <span
          className={cn(
            'absolute left-0.5 h-4 w-4 rounded-full shadow-sm transition-transform duration-med ease-snappy',
            checked ? 'translate-x-3 bg-brand-contrast' : 'translate-x-0 bg-surface',
          )}
        />
      </span>
      {(label || description) && (
        <span className="flex flex-col leading-tight">
          {label ? <span className="text-13 text-fg-strong">{label}</span> : null}
          {description ? <span className="text-xs text-fg-muted">{description}</span> : null}
        </span>
      )}
    </label>
  );
}
